import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Filter,
  Grid,
  List,
  Copy,
  Check,
  FileIcon,
  Image,
  Video,
  FileText,
  Music
} from 'lucide-react';
import Modal from './Modal';
import { PageLoader } from './common';
import { Asset as ApiAsset, tenantApi } from '../lib/tenant-api';

interface AssetSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetsSelected: (data: { mode: 'script' | 'tags' | 'carousel'; markdownStrings?: string[]; tags?: string[]; carouselMarkup?: string }) => void;
}

// Convert API Asset to UI Asset (reusing logic from AssetsManagement)
interface Asset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'audio' | 'other';
  size: number;
  uploadDate: string;
  url: string;
  thumbnailUrl?: string;
  description?: string;
  tags: string[];
}

const convertApiAsset = (apiAsset: ApiAsset): Asset => {
  return {
    id: apiAsset.id,
    name: apiAsset.file_name,
    type: apiAsset.asset_type as any,
    size: apiAsset.file_size,
    uploadDate: apiAsset.created_at,
    url: apiAsset.custom_metadata.public_url,
    thumbnailUrl: apiAsset.asset_type === 'image'
      ? apiAsset.custom_metadata.public_url
      : undefined,
    description: apiAsset.custom_metadata.description,
    tags: apiAsset.tags || []
  };
};

type FilterType = 'all' | 'image' | 'video' | 'document' | 'audio' | 'other';
type ViewMode = 'grid' | 'list';

const AssetSelectorModal: React.FC<AssetSelectorModalProps> = ({
  isOpen,
  onClose,
  onAssetsSelected
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [copiedAssets, setCopiedAssets] = useState<Set<string>>(new Set());
  const [assetSelectionMode, setAssetSelectionMode] = useState<'script' | 'tags' | 'carousel'>('script');
  const [assetTags, setAssetTags] = useState<string[]>([]);

  // Load assets
  useEffect(() => {
    if (isOpen) {
      loadAssets();
    }
  }, [isOpen]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiAssets = await tenantApi.getAssets();
      setAssets(apiAssets.map(convertApiAsset));
    } catch (err) {
      console.error('Failed to load assets:', err);
      setError('Failed to load assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search assets
  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === 'all' || asset.type === filterType;

      // In carousel mode, only show images and videos
      if (assetSelectionMode === 'carousel') {
        const isMediaFile = asset.type === 'image' || asset.type === 'video';
        return matchesSearch && matchesFilter && isMediaFile;
      }

      return matchesSearch && matchesFilter;
    });
  }, [assets, searchQuery, filterType, assetSelectionMode]);

  const getFilterCounts = () => {
    return {
      all: assets.length,
      image: assets.filter(a => a.type === 'image').length,
      video: assets.filter(a => a.type === 'video').length,
      document: assets.filter(a => a.type === 'document').length,
      audio: assets.filter(a => a.type === 'audio').length,
      other: assets.filter(a => a.type === 'other').length
    };
  };

  const filterCounts = getFilterCounts();

  // Toggle asset selection
  const toggleAssetSelection = (assetId: string) => {
    const newSelected = new Set(selectedAssets);
    const wasSelected = newSelected.has(assetId);

    if (wasSelected) {
      newSelected.delete(assetId);
    } else {
      newSelected.add(assetId);
    }

    setSelectedAssets(newSelected);

    // Automatically extract and add tags in tags mode
    if (assetSelectionMode === 'tags') {
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        if (wasSelected) {
          // Asset was deselected - remove its tags (but keep manually added ones)
          const assetSpecificTags = extractTagsFromAsset(asset);
          setAssetTags(prevTags =>
            prevTags.filter(tag => !assetSpecificTags.includes(tag))
          );
        } else {
          // Asset was selected - add its tags
          const newTags = extractTagsFromAsset(asset);
          setAssetTags(prevTags => {
            const combinedTags = [...prevTags, ...newTags];
            return [...new Set(combinedTags)]; // Remove duplicates
          });
        }
      }
    }
  };

  // Generate markdown for an asset (client-side only, no API call)
  const generateMarkdownForAsset = (asset: Asset): string => {
    const displayName = asset.name.split('.')[0];
    switch (asset.type) {
      case 'image':
      case 'video':
        return `![${displayName}](${asset.url})`;
      case 'document':
      case 'audio':
      default:
        return `[${displayName}](${asset.url})`;
    }
  };

  // Copy markdown to clipboard
  const copyMarkdownToClipboard = async (asset: Asset) => {
    try {
      const markdown = generateMarkdownForAsset(asset);
      await navigator.clipboard.writeText(markdown);

      setCopiedAssets(prev => new Set(prev).add(asset.id));
      setTimeout(() => {
        setCopiedAssets(prev => {
          const newSet = new Set(prev);
          newSet.delete(asset.id);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy markdown:', err);
    }
  };

  // Extract tags from asset - prioritize database tags, fallback to generated tags
  const extractTagsFromAsset = (asset: Asset): string[] => {
    const tags: string[] = [];

    // First, use actual database tags if they exist
    if (asset.tags && asset.tags.length > 0) {
      tags.push(...asset.tags);
    } else {
      // Fallback: generate tags from asset type and filename if no database tags exist
      tags.push(asset.type);

      // Extract meaningful words from filename (remove extension and split by common separators)
      const nameWithoutExt = asset.name.replace(/\.[^/.]+$/, '');
      const words = nameWithoutExt
        .toLowerCase()
        .split(/[-_\s.]+/)
        .filter(word => word.length > 2 && !['img', 'image', 'photo', 'pic', 'file', 'doc', 'video', 'vid'].includes(word));

      tags.push(...words);

      // Add description keywords if available
      if (asset.description) {
        const descWords = asset.description
          .toLowerCase()
          .split(/\s+/)
          .filter(word => word.length > 3 && !['the', 'and', 'for', 'with', 'this', 'that'].includes(word));
        tags.push(...descWords.slice(0, 3)); // Limit to first 3 meaningful words
      }
    }

    // Remove duplicates and return
    return [...new Set(tags)];
  };

  // Copy tags from selected assets
  const copyTagsFromSelectedAssets = () => {
    const selectedAssetsList = assets.filter(asset => selectedAssets.has(asset.id));
    const allTags: string[] = [];

    selectedAssetsList.forEach(asset => {
      const assetTags = extractTagsFromAsset(asset);
      allTags.push(...assetTags);
    });

    // Remove duplicates and combine with existing tags
    const uniqueTags = [...new Set([...assetTags, ...allTags])];
    setAssetTags(uniqueTags);

    // Show visual feedback
    setCopiedAssets(new Set(selectedAssets));
    setTimeout(() => {
      setCopiedAssets(new Set());
    }, 2000);
  };

  // Generate carousel markup (matches RichTextEditor MediaCarouselDialog format exactly)
  const generateCarouselMarkup = (selectedAssetsList: Asset[]): string => {
    const mediaItems = selectedAssetsList.map(asset => {
      const displayName = asset.name.split('.')[0];
      return `<media-item>![${displayName}](${asset.url})</media-item>`;
    });

    return `<carousel>\n${mediaItems.join('\n')}\n</carousel>`;
  };

  // Handle confirm action based on mode
  const handleConfirm = () => {
    try {
      if (assetSelectionMode === 'script') {
        // Script mode: generate markdown for selected assets (client-side)
        if (selectedAssets.size === 0) {
          setError('Please select at least one asset.');
          return;
        }

        const selectedAssetsList = assets.filter(asset => selectedAssets.has(asset.id));
        const markdownStrings = selectedAssetsList.map(asset => generateMarkdownForAsset(asset));

        onAssetsSelected({
          mode: 'script',
          markdownStrings
        });
      } else if (assetSelectionMode === 'tags') {
        // Tags mode: auto-select assets matching tags and insert as markdown
        if (assetTags.length === 0) {
          setError('Please add at least one tag.');
          return;
        }

        // Find assets that match any of the entered tags (using the same tag extraction as the UI)
        // Also include any explicitly selected assets
        const matchedAssets = assets.filter(asset => {
          if (selectedAssets.has(asset.id)) return true;
          const assetTagsList = extractTagsFromAsset(asset);
          return assetTagsList.some(tag =>
            assetTags.some(enteredTag => tag.toLowerCase().includes(enteredTag.toLowerCase()))
          );
        });

        if (matchedAssets.length === 0) {
          setError('No assets match the entered tags. Try different tags or select assets manually.');
          return;
        }

        const markdownStrings = matchedAssets.map(asset => generateMarkdownForAsset(asset));

        onAssetsSelected({
          mode: 'script',
          markdownStrings
        });
      } else {
        // Carousel mode: generate carousel markup
        if (selectedAssets.size < 2) {
          setError('Please select at least 2 media items for the carousel.');
          return;
        }

        if (selectedAssets.size > 12) {
          setError('Please select no more than 12 media items for the carousel.');
          return;
        }

        const selectedAssetsList = assets.filter(asset => selectedAssets.has(asset.id));

        // Validate only media files
        const nonMediaAssets = selectedAssetsList.filter(asset => asset.type !== 'image' && asset.type !== 'video');
        if (nonMediaAssets.length > 0) {
          setError('Carousel can only contain images and videos.');
          return;
        }

        const carouselMarkup = generateCarouselMarkup(selectedAssetsList);

        onAssetsSelected({
          mode: 'carousel',
          carouselMarkup
        });
      }

      handleClose();
    } catch (err) {
      console.error('Failed to process assets:', err);
      setError('Failed to process assets. Please try again.');
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedAssets(new Set());
    setCopiedAssets(new Set());
    setSearchQuery('');
    setFilterType('all');
    setError(null);
    onClose();
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get icon for asset type
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="h-5 w-5" />;
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'document':
        return <FileText className="h-5 w-5" />;
      case 'audio':
        return <Music className="h-5 w-5" />;
      default:
        return <FileIcon className="h-5 w-5" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Select Assets for Primer Question"
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-sm text-gray-500">
            {assetSelectionMode === 'script' && selectedAssets.size > 0 && (
              <span>{selectedAssets.size} asset(s) selected</span>
            )}
            {assetSelectionMode === 'tags' && assetTags.length > 0 && (
              <span>{assetTags.length} tag(s) added</span>
            )}
            {assetSelectionMode === 'carousel' && selectedAssets.size > 0 && (
              <span>
                {selectedAssets.size} media item(s) selected
                {selectedAssets.size < 2 && <span className="text-red-600"> (min 2)</span>}
                {selectedAssets.size > 12 && <span className="text-red-600"> (max 12)</span>}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                (assetSelectionMode === 'script' && selectedAssets.size === 0) ||
                (assetSelectionMode === 'tags' && assetTags.length === 0) ||
                (assetSelectionMode === 'carousel' && (selectedAssets.size < 2 || selectedAssets.size > 12))
              }
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assetSelectionMode === 'script'
                ? `Insert Markdown (${selectedAssets.size})`
                : assetSelectionMode === 'tags'
                  ? `Insert by Tags (${assetTags.length})`
                  : `Insert Carousel (${selectedAssets.size})`
              }
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 ">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Asset Selection Mode */}
        <div className="border  p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">Asset Selection Mode</h4>
          </div>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="radio"
                id="mode_script"
                name="asset_selection_mode"
                value="script"
                checked={assetSelectionMode === 'script'}
                onChange={(e) => setAssetSelectionMode(e.target.value as 'script' | 'tags' | 'carousel')}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <label htmlFor="mode_script" className="ml-2 block text-sm text-gray-700">
                Script Mode - Generate markdown from selected assets
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="mode_tags"
                name="asset_selection_mode"
                value="tags"
                checked={assetSelectionMode === 'tags'}
                onChange={(e) => setAssetSelectionMode(e.target.value as 'script' | 'tags' | 'carousel')}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <label htmlFor="mode_tags" className="ml-2 block text-sm text-gray-700">
                Tag-based Mode - Auto-select assets by tags
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="mode_carousel"
                name="asset_selection_mode"
                value="carousel"
                checked={assetSelectionMode === 'carousel'}
                onChange={(e) => setAssetSelectionMode(e.target.value as 'script' | 'tags' | 'carousel')}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
              />
              <label htmlFor="mode_carousel" className="ml-2 block text-sm text-gray-700">
                Carousel Mode - Generate carousel from selected images/videos
              </label>
            </div>
            <p className="text-xs text-gray-500">
              {assetSelectionMode === 'script'
                ? 'Select specific assets below to insert markdown into your answer.'
                : assetSelectionMode === 'tags'
                  ? 'Enter tags below to find matching assets and insert their markdown into your answer.'
                  : 'Select 2-12 images and videos to create a carousel. Only media files will be shown below.'
              }
            </p>
          </div>
        </div>

        {/* Tags Input (only show when tags mode is selected) */}
        {assetSelectionMode === 'tags' && (
          <div className="border  p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset Tags
            </label>
            <div className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Add tags... (press Enter to add)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      const tag = input.value.trim().toLowerCase();
                      if (tag && !assetTags.includes(tag)) {
                        setAssetTags([...assetTags, tag]);
                        input.value = '';
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Current Tags */}
              {assetTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {assetTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => {
                          setAssetTags(assetTags.filter((_, i) => i !== index));
                        }}
                        className="ml-1 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500">
                Enter tags to find matching assets. Matched assets will be inserted as markdown into your answer.
              </p>
            </div>
          </div>
        )}

        {/* Search and Filter Bar & Assets Grid - show in all modes */}
        {(assetSelectionMode === 'script' || assetSelectionMode === 'tags' || assetSelectionMode === 'carousel') && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300  leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Filters */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="h-5 w-5 text-gray-400" />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as FilterType)}
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm "
                  >
                    <option value="all">All Types ({filterCounts.all})</option>
                    <option value="image">Images ({filterCounts.image})</option>
                    <option value="video">Videos ({filterCounts.video})</option>
                    <option value="document">Documents ({filterCounts.document})</option>
                    <option value="audio">Audio ({filterCounts.audio})</option>
                    <option value="other">Other ({filterCounts.other})</option>
                  </select>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-100  p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2  ${viewMode === 'grid'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2  ${viewMode === 'list'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Assets Grid/List */}
            {loading ? (
              <PageLoader fullScreen={false} />
            ) : filteredAssets.length > 0 ? (
              <div className={`max-h-96 overflow-y-auto ${viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
                : 'space-y-3'
                }`}>
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className={`border  p-4 cursor-pointer transition-all ${selectedAssets.has(asset.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                      } ${viewMode === 'list' ? 'flex items-center space-x-4' : ''}`}
                    onClick={() => toggleAssetSelection(asset.id)}
                  >
                    {/* Checkbox */}
                    <div className={`${viewMode === 'list' ? '' : 'mb-3'} flex-shrink-0`}>
                      <input
                        type="checkbox"
                        checked={selectedAssets.has(asset.id)}
                        onChange={() => toggleAssetSelection(asset.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 "
                      />
                    </div>

                    {/* Asset Preview/Icon */}
                    <div className={`${viewMode === 'list' ? '' : 'mb-3'} flex-shrink-0`}>
                      {asset.thumbnailUrl ? (
                        <img
                          src={asset.thumbnailUrl}
                          alt={asset.name}
                          className={`object-cover  ${viewMode === 'list' ? 'w-12 h-12' : 'w-full h-32'
                            }`}
                        />
                      ) : (
                        <div className={`bg-gray-200  flex items-center justify-center text-gray-400 ${viewMode === 'list' ? 'w-12 h-12' : 'w-full h-32'
                          }`}>
                          {getAssetIcon(asset.type)}
                        </div>
                      )}
                    </div>

                    {/* Asset Info */}
                    <div className={`${viewMode === 'list' ? 'flex-1' : ''} min-w-0`}>
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {asset.name}
                      </h4>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500 uppercase">
                          {asset.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatFileSize(asset.size)}
                        </span>
                      </div>

                      {/* Copy Markdown Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyMarkdownToClipboard(asset);
                        }}
                        className={`mt-2 inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium  text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${copiedAssets.has(asset.id) ? 'bg-green-100 text-green-700' : ''
                          }`}
                      >
                        {copiedAssets.has(asset.id) ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Markdown
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No assets found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery || filterType !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Upload some assets first to use them in primer questions.'
                  }
                </p>
              </div>
            )}

            {/* Copy Tags Button - only show in tags mode when assets are selected */}
            {assetSelectionMode === 'tags' && selectedAssets.size > 0 && (
              <div className="flex justify-center pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={copyTagsFromSelectedAssets}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200  hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Tags from Selected Assets ({selectedAssets.size})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AssetSelectorModal;
